import React from "react";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";
import { BodyTyp } from "../../../../../../../../components/Typography/Typography";
import { GRADE_LABEL } from "../../../../../../../../constants/Dictionary";
import {
  FontWeightEnum,
  IconSizeEnum,
} from "../../../../../../../../types/AppTypes";
import {
  StyledManageGrades,
  StyledManageGradesContainer,
} from "./GradesDnd.styled";
import { theme } from "../../../../../../../../constants/StyledComponents";
import { IconManager } from "../../../../../../../../components/core/IconManager/IconManager";
import { ReactComponent as AddIcon } from "../../../../../../../../icons/add.svg";
import { ContainerTitleGradePath } from "../PathsDnd/PathsDnd.styled";
import { GradePathItem } from "../../../../../../../../components/GradePathItem/PathGradeItem";
import { GenericPathGradeItem, TypeItemPrimary } from "../ManagePathsGrades";
import { NewPathGradeConfigItem } from "../PathsDnd/PathsDnd";

type GradesDndProps = {
  isEditable: boolean;
  gradeItems: GenericPathGradeItem[];
  setGradeItems: (x: GenericPathGradeItem[]) => void;
  setPathGradeItems: (
    x: (prev: NewPathGradeConfigItem[][]) => NewPathGradeConfigItem[][],
  ) => void;
  setNewItemIds: (x: number | null) => void;
  newItemIds: number | null;
  setEditingId: (
    x: {
      id: number;
      type: TypeItemPrimary;
    } | null,
  ) => void;
  editingId: {
    id: number;
    type: string;
  } | null;
  handleEdit: (x: number, typeItemPrimary: TypeItemPrimary) => void;
  handleSave: (id: number, typeItemPrimary: TypeItemPrimary) => void;
  handleDelete: (id: number, typeItemPrimary: TypeItemPrimary) => void;
  handleInputChange: (text: string) => void;
  setNewNamePrimary: (x: string) => void;
  newNamePrimary: string;
  setIsUpdatingAnItem: (x: boolean) => void;
  isUpdatingAnItem: boolean;
  handleCancel: (
    item: GenericPathGradeItem,
    type: TypeItemPrimary,
    isNewItem: boolean,
  ) => void;
  editingChildrenItem: boolean;
  activeChildrenItem: boolean;
};

export const GradesDnd = ({
  isEditable,
  gradeItems,
  setGradeItems,
  setPathGradeItems,
  newItemIds,
  setNewItemIds,
  setEditingId,
  editingId,
  handleEdit,
  handleSave,
  handleDelete,
  handleInputChange,
  setNewNamePrimary,
  newNamePrimary,
  setIsUpdatingAnItem,
  isUpdatingAnItem,
  handleCancel,
  editingChildrenItem,
  activeChildrenItem,
}: GradesDndProps) => {
  const onDragGradesEnd = (result: DropResult) => {
    if (!result.destination) return;

    const reversedItems = [...gradeItems].reverse();
    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;
    const [movedItem] = reversedItems.splice(sourceIndex, 1);
    reversedItems.splice(destinationIndex, 0, movedItem);
    const newOrder = reversedItems.reverse();

    setGradeItems(newOrder);
  };

  const handleCreateNewGrade = () => {
    const lastId =
      gradeItems.length > 0 ? gradeItems[gradeItems.length - 1].id + 1 : 0;

    const newElement: GenericPathGradeItem = {
      id: lastId,
      name: "",
      position: gradeItems.length + 1,
    };

    setNewItemIds(lastId);
    setGradeItems([...gradeItems, newElement]);
    setNewNamePrimary("");
    setEditingId({ id: lastId, type: TypeItemPrimary.GRADE });
    setPathGradeItems((prevItems) => {
      return prevItems.map((subArray) => [null, ...subArray]);
    });
    setIsUpdatingAnItem(true);
  };

  return (
    <StyledManageGrades>
      <ContainerTitleGradePath>
        <BodyTyp variant="1" fontWeight={FontWeightEnum.SEMIBOLD}>
          {GRADE_LABEL}
        </BodyTyp>
        {isEditable && (
          <IconManager
            size={IconSizeEnum.SMALL}
            onClick={!isUpdatingAnItem ? handleCreateNewGrade : undefined}
            color={!isUpdatingAnItem ? theme.colors.black : theme.colors.grey_3}
          >
            <AddIcon />
          </IconManager>
        )}
      </ContainerTitleGradePath>
      <DragDropContext onDragEnd={onDragGradesEnd}>
        <Droppable droppableId="droppable">
          {(provided, snapshot) => (
            <StyledManageGradesContainer
              {...provided.droppableProps}
              ref={provided.innerRef}
              $isDraggingOver={snapshot.isDraggingOver}
            >
              {[...gradeItems].reverse().map((item, index) => {
                const descendingNumber = gradeItems.length - index;

                return (
                  <Draggable
                    key={item.id.toString()}
                    draggableId={item.id.toString()}
                    index={index}
                    isDragDisabled={!isEditable}
                  >
                    {(provided, snapshot) => (
                      <GradePathItem
                        index={descendingNumber - 1}
                        provided={provided}
                        snapshot={snapshot}
                        editingId={editingId}
                        isEditable={isEditable}
                        newItemIds={newItemIds}
                        item={item}
                        newItemName={newNamePrimary}
                        handleEdit={handleEdit}
                        handleSave={handleSave}
                        handleDelete={handleDelete}
                        handleInputChange={handleInputChange}
                        typeItemPrimary={TypeItemPrimary.GRADE}
                        isUpdatingAnItem={isUpdatingAnItem}
                        handleCancel={handleCancel}
                        editingChildrenItem={editingChildrenItem}
                        activeChildrenItem={activeChildrenItem}
                      />
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </StyledManageGradesContainer>
          )}
        </Droppable>
      </DragDropContext>
    </StyledManageGrades>
  );
};
